import { useGetTaxonomyChildren } from '@/hooks/api/taxonomy/useGetTaxonomyChildren';
import { useTreeSearch } from '@/hooks/api/taxonomy/useTreeSearch';
import { useCreateTaxonomyNode } from '@/hooks/api/taxonomy/useCreateTaxonomyNode';
import { useRenameTaxonomyNode } from '@/hooks/api/taxonomy/useRenameTaxonomyNode';
import { useDeleteTaxonomyNode } from '@/hooks/api/taxonomy/useDeleteTaxonomyNode';
import { DeleteModal } from '@/components/modals/DeleteModal';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { Add, AccountTree, FolderOpen, Label, UnfoldMore, UnfoldLess, Search, Clear } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TaxonomyNodeLevel } from './TaxonomyNodeLevel';
import { AddNodeInput } from './AddNodeInput';
import type { TaxonomyTreeProperties } from './taxonomyTreeConstants';

// The page panel already draws the border and the surface; the tree only owns its own spacing, so
// the two do not stack into a box inside a box.
const TreeContainer = styled(Box)({
  padding: '16px',
  paddingLeft: 0,
});

export const TaxonomyTree = ({ taxonomyId }: { taxonomyId: string }) => {
  const {
    nodes: rootNodes,
    isLoading,
    error: rootError,
    hasNextPage: rootHasNextPage,
    fetchNextPage: rootFetchNextPage,
    isFetchingNextPage: rootIsFetchingNextPage,
  } = useGetTaxonomyChildren(taxonomyId, 'root');
  const { createNode, isCreating, createError } = useCreateTaxonomyNode(taxonomyId);
  const { renameNode, isRenaming, renameError } = useRenameTaxonomyNode(taxonomyId);

  const [addingChildOfId, setAddingChildOfId] = useState<string | undefined>();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [expandAllActive, setExpandAllActive] = useState(false);
  const [renamingNodeId, setRenamingNodeId] = useState<string | undefined>();
  const [deletingNode, setDeletingNode] = useState<{ id: string; name: string; hasChildren: boolean } | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputReference = useRef<HTMLInputElement>(null);
  const scrollContainerReference = useRef<HTMLElement>(null);
  const addingChildOfIdReference = useRef(addingChildOfId);
  addingChildOfIdReference.current = addingChildOfId;

  const { searchResults, isSearching, isSearchPending, searchVisibleIds, searchMatchIds, searchExpandedIds } =
    useTreeSearch(taxonomyId, searchQuery);

  useEffect(() => {
    if (searchExpandedIds.length === 0) return;
    setExpandedItems(previous => {
      const newIds = searchExpandedIds.filter(id => !previous.includes(id));
      return newIds.length > 0 ? [...previous, ...newIds] : previous;
    });
  }, [searchExpandedIds]);

  const expandedSet = useMemo(() => new Set(expandedItems), [expandedItems]);
  const isItemExpanded = useCallback((id: string) => expandedSet.has(id), [expandedSet]);

  const addExpandedItems = useCallback((ids: string[]) => {
    startTransition(() => {
      setExpandedItems(previous => {
        const newIds = ids.filter(id => !previous.includes(id));
        return newIds.length > 0 ? [...previous, ...newIds] : previous;
      });
    });
  }, []);

  const handleExpandAll = () => {
    setExpandAllActive(true);
    if (rootHasNextPage && !rootIsFetchingNextPage) {
      rootFetchNextPage();
    }
    if (rootNodes.length > 0) {
      addExpandedItems(rootNodes.filter(n => n.hasChildren).map(n => n.id));
    }
  };

  useEffect(() => {
    if (!expandAllActive) return;
    if (rootHasNextPage && !rootIsFetchingNextPage) {
      rootFetchNextPage();
    }
    if (rootNodes.length > 0) {
      addExpandedItems(rootNodes.filter(n => n.hasChildren).map(n => n.id));
    }
  }, [expandAllActive, rootNodes, rootHasNextPage, rootIsFetchingNextPage, rootFetchNextPage, addExpandedItems]);

  const handleCollapseAll = useCallback(() => {
    setExpandAllActive(false);
    setExpandedItems([]);
  }, []);

  const handleAddChild = useCallback((parentId: string) => {
    setAddingChildOfId(parentId);
    if (parentId !== 'root') {
      setExpandedItems(previous => (previous.includes(parentId) ? previous : [...previous, parentId]));
    }
  }, []);

  const handleSubmit = useCallback(
    (name: string) => {
      const parentNodeId = addingChildOfIdReference.current === 'root' ? undefined : addingChildOfIdReference.current;
      createNode({ name, parentNodeId }, { onSuccess: () => setAddingChildOfId(undefined) });
    },
    [createNode]
  );

  const handleCancel = useCallback(() => setAddingChildOfId(undefined), []);

  const handleStartRename = useCallback((nodeId: string) => setRenamingNodeId(nodeId), []);

  const handleRenameNode = useCallback(
    (nodeId: string, newName: string) => {
      renameNode({ nodeId, name: newName }, { onSuccess: () => setRenamingNodeId(undefined) });
    },
    [renameNode]
  );

  const handleCancelRename = useCallback(() => setRenamingNodeId(undefined), []);

  const handleDeleteNode = useCallback((nodeId: string, nodeName: string, hasChildren: boolean) => {
    setDeletingNode({ id: nodeId, name: nodeName, hasChildren });
  }, []);

  const treeProperties: TaxonomyTreeProperties = useMemo(
    () => ({
      taxonomyId,
      isItemExpanded,
      addExpandedItems,
      expandAllActive,
      addingChildOfId,
      isCreating,
      onAddChild: handleAddChild,
      onSubmitChild: handleSubmit,
      onCancelAdd: handleCancel,
      renamingNodeId,
      isRenaming,
      onStartRename: handleStartRename,
      onRenameNode: handleRenameNode,
      onCancelRename: handleCancelRename,
      onDeleteNode: handleDeleteNode,
      scrollContainerReference,
      searchVisibleIds,
      searchMatchIds,
    }),
    [
      taxonomyId,
      isItemExpanded,
      addExpandedItems,
      expandAllActive,
      addingChildOfId,
      isCreating,
      searchVisibleIds,
      searchMatchIds,
      handleAddChild,
      handleSubmit,
      handleCancel,
      renamingNodeId,
      isRenaming,
      handleStartRename,
      handleRenameNode,
      handleCancelRename,
      handleDeleteNode,
    ]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const isAddingRoot = addingChildOfId === 'root';
  const displayError = createError || renameError || rootError;

  return (
    <Box>
      {displayError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {displayError.message}
        </Alert>
      )}
      {rootNodes.length === 0 && !isAddingRoot ? (
        <TreeContainer sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <AccountTree sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant='body2' color='text.secondary'>
            This taxonomy is empty.
          </Typography>
          <Button variant='outlined' size='small' startIcon={<Add />} onClick={() => handleAddChild('root')}>
            Add Category
          </Button>
        </TreeContainer>
      ) : (
        <TreeContainer ref={scrollContainerReference} sx={{ maxHeight: '70vh', overflow: 'auto', pt: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              pt: '16px',
              pb: 1.5,
              mx: -2,
              px: 2,
              mt: '-16px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flexShrink: 1 }}>
              <Tooltip title='Groups other categories. Cannot have assets assigned directly.' arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default', whiteSpace: 'nowrap' }}>
                  <FolderOpen sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant='caption' color='text.secondary'>
                    Category
                  </Typography>
                </Box>
              </Tooltip>
              <Tooltip title='The most specific level. Assets are labeled with one of these.' arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default', whiteSpace: 'nowrap' }}>
                  <Label sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant='caption' color='text.secondary'>
                    Label
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InputAdornment
                position='start'
                sx={{
                  overflow: 'hidden',
                  width: searchOpen ? 240 : 0,
                  opacity: searchOpen ? 1 : 0,
                  transition: 'width 0.25s ease, opacity 0.2s ease',
                  m: 0,
                }}
              >
                <TextField
                  inputRef={searchInputReference}
                  size='small'
                  placeholder='Search taxonomy...'
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  onBlur={() => {
                    if (!searchQuery) setSearchOpen(false);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Escape') {
                      setSearchQuery('');
                      setSearchOpen(false);
                    }
                  }}
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery ? (
                        <InputAdornment position='end'>
                          <IconButton
                            size='small'
                            onClick={() => {
                              setSearchQuery('');
                              searchInputReference.current?.focus();
                            }}
                            edge='end'
                          >
                            <Clear sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                      sx: { fontSize: '0.875rem', borderRadius: 5, height: 32 },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  }}
                />
              </InputAdornment>
              <Tooltip title='Search' arrow>
                <IconButton
                  size='small'
                  aria-label='Search'
                  onClick={() => {
                    setSearchOpen(true);
                    setTimeout(() => searchInputReference.current?.focus(), 50);
                  }}
                  sx={{ color: searchOpen ? 'primary.main' : undefined }}
                >
                  <Search fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title='Collapse all' arrow>
                <IconButton onClick={handleCollapseAll} size='small' aria-label='Collapse all'>
                  <UnfoldLess fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title='Expand all' arrow>
                <IconButton onClick={handleExpandAll} size='small' aria-label='Expand all'>
                  {expandAllActive && rootIsFetchingNextPage ? (
                    <CircularProgress size={16} />
                  ) : (
                    <UnfoldMore fontSize='small' />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title='Add Category' arrow>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => handleAddChild('root')}
                    disabled={isAddingRoot}
                    aria-label='Add Category'
                  >
                    <Add fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          {isSearching && searchResults.length === 0 && isSearchPending && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {isSearching && searchResults.length === 0 && !isSearchPending && (
            <Typography variant='body2' color='text.secondary' sx={{ py: 3, textAlign: 'center' }}>
              No matching categories found.
            </Typography>
          )}
          {(!isSearching || searchResults.length > 0) && (
            <>
              {isAddingRoot && <AddNodeInput onSubmit={handleSubmit} onCancel={handleCancel} isCreating={isCreating} />}
              <SimpleTreeView
                expandedItems={expandedItems}
                onExpandedItemsChange={(_event, itemIds) => {
                  startTransition(() => setExpandedItems(itemIds));
                  if (expandAllActive) setExpandAllActive(false);
                }}
                sx={{
                  '& .MuiTreeItem-content': { borderRadius: 1, py: 0.25 },
                  '& .MuiTreeItem-iconContainer': { color: 'text.secondary' },
                  '& .MuiTreeItem-groupTransition': {
                    ml: 2.5,
                    pl: 1.5,
                    borderLeft: '2px solid',
                    borderColor: 'divider',
                    position: 'relative',
                  },
                }}
              >
                <TaxonomyNodeLevel parentNodeId='root' depth={0} {...treeProperties} />
              </SimpleTreeView>
            </>
          )}
        </TreeContainer>
      )}
      {deletingNode && (
        <DeleteModal
          useHook={useDeleteTaxonomyNode}
          hookParams={{ taxonomyId }}
          mutateParams={deletingNode.id}
          isOpen={Boolean(deletingNode)}
          onClose={() => setDeletingNode(undefined)}
          title='Delete Category'
          itemName={deletingNode.name}
          item={deletingNode}
          alertSeverity={deletingNode.hasChildren ? 'warning' : undefined}
          alertMessage={
            deletingNode.hasChildren
              ? 'This category has subcategories. Deleting it will also delete all subcategories within it.'
              : undefined
          }
        />
      )}
    </Box>
  );
};
